import { useEffect, useState } from "react";
import { UserRound } from "lucide-react";

import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import type { Agent, OrderFull } from "@/lib/orders";

/**
 * Who follows the order. On a new order the choice travels with the form;
 * on a saved order it applies immediately (any status except cancelled), so
 * a sent or signed quote can still be handed to a colleague.
 */
export function AgentCard({
  order,
  value,
  dirty,
  onPick,
  onChanged,
}: {
  order: OrderFull | null;
  value: string | null | undefined;
  dirty: boolean;
  onPick: (id: string | null) => void;
  onChanged: (o: OrderFull) => void;
}) {
  const { user } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.orderAgents().then(setAgents).catch(() => setAgents([]));
  }, []);

  const locked = Boolean(order) && (order?.status === "cancelled" || dirty);
  const current = order ? (order.assigneeId ?? "") : (value ?? "");

  async function change(id: string) {
    setMsg(null);
    if (!order) {
      onPick(id || null);
      return;
    }
    if (!id || id === order.assigneeId) return;
    setBusy(true);
    try {
      onChanged(await api.assignOrder(order.id, id));
      setMsg("Assigned.");
    } catch {
      setMsg("Could not assign.");
    } finally {
      setBusy(false);
    }
  }

  const options = order ? agents : agents.filter((a) => a.id !== user?.id);

  return (
    <section className="glass-panel mt-4 p-5">
      <div className="brand-kicker">Sales agent</div>
      <p className="mt-1 text-xs text-ice/60">
        {order
          ? "Who follows this order. Changes apply at once, also on sent or signed orders."
          : "Defaults to you. Pick a colleague to hand the order over."}
      </p>
      <label className="mt-3 block">
        <span className="field-label">
          <UserRound className="mr-1 inline h-3 w-3" />
          Agent
        </span>
        <select className="field" disabled={busy || locked} value={current} onChange={(e) => change(e.target.value)}>
          {!order && <option value="" className="bg-night">Me ({user?.name})</option>}
          {order && !options.some((a) => a.id === current) && (
            <option value={current} className="bg-night">{order.assigneeName ?? "—"}</option>
          )}
          {options.map((a) => (
            <option key={a.id} value={a.id} className="bg-night">
              {a.name}{a.role === "admin" ? " · admin" : ""}
            </option>
          ))}
        </select>
      </label>
      {order && dirty && <p className="mt-2 text-xs text-ice/60">Save your changes first, then reassign.</p>}
      {msg && <p className="mt-2 text-xs text-goldbright">{msg}</p>}
    </section>
  );
}
